import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calculator, 
  DollarSign, 
  TrendingUp, 
  Package, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  BarChart3,
  Target,
  Save,
  RefreshCw,
  Plus,
  Minus,
  Percent,
  ShoppingCart,
  Users,
  Clock,
  Lightbulb
} from 'lucide-react';

const PriceCalculator = () => {
  const navigate = useNavigate();
  const [calculation, setCalculation] = useState({
    // Custos diretos
    costPrice: '',
    packaging: '',
    transportation: '',
    storage: '',
    
    // Custos indiretos (%)
    operationalCosts: 15, // %
    taxes: 8, // %
    marketingCosts: 5, // %
    
    // Margem de lucro
    desiredMargin: 30, // %
    
    // Informações do produto
    productName: '',
    category: '',
    unitType: 'unidade',
    
    // Análise de mercado
    competitorPrice: '',
    marketPosition: 'medio' // baixo, medio, premium
  });

  const [results, setResults] = useState({
    totalCost: 0,
    suggestedPrice: 0,
    finalPrice: 0,
    profitMargin: 0,
    profitValue: 0,
    competitorComparison: 0,
    priceRecommendation: '',
    alerts: []
  });

  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Categorias predefinidas com margens sugeridas
  const categories = {
    'salgados': { margin: 40, name: 'Salgados', operationalCosts: 12 },
    'doces': { margin: 45, name: 'Doces', operationalCosts: 10 },
    'bebidas': { margin: 35, name: 'Bebidas', operationalCosts: 8 },
    'sanduiches': { margin: 38, name: 'Sanduíches', operationalCosts: 15 },
    'frutas': { margin: 30, name: 'Frutas', operationalCosts: 20 },
    'outros': { margin: 30, name: 'Outros', operationalCosts: 15 }
  };

  const marketPositions = {
    'baixo': { multiplier: 0.9, name: 'Preço Baixo (Competitivo)' },
    'medio': { multiplier: 1.0, name: 'Preço Médio (Equilibrado)' },
    'premium': { multiplier: 1.15, name: 'Preço Premium (Qualidade)' }
  };

  useEffect(() => {
    calculatePrice();
  }, [calculation]);

  const calculatePrice = () => {
    const costPrice = parseFloat(calculation.costPrice) || 0;
    const packaging = parseFloat(calculation.packaging) || 0;
    const transportation = parseFloat(calculation.transportation) || 0;
    const storage = parseFloat(calculation.storage) || 0;
    
    // Custo direto total
    const directCost = costPrice + packaging + transportation + storage;
    
    // Custos indiretos (aplicados sobre o custo direto)
    const operationalCost = directCost * (calculation.operationalCosts / 100);
    const taxCost = directCost * (calculation.taxes / 100);
    const marketingCost = directCost * (calculation.marketingCosts / 100);
    
    // Custo total
    const totalCost = directCost + operationalCost + taxCost + marketingCost;
    
    // Preço sugerido com margem
    const suggestedPrice = totalCost / (1 - calculation.desiredMargin / 100);
    
    // Ajuste por posição de mercado
    const marketMultiplier = marketPositions[calculation.marketPosition].multiplier;
    const finalPrice = suggestedPrice * marketMultiplier;
    
    // Margem real e lucro
    const profitValue = finalPrice - totalCost;
    const profitMargin = totalCost > 0 ? (profitValue / finalPrice) * 100 : 0;
    
    // Comparação com concorrente
    const competitorPrice = parseFloat(calculation.competitorPrice) || 0;
    const competitorComparison = competitorPrice > 0 ? 
      ((finalPrice - competitorPrice) / competitorPrice) * 100 : 0;
    
    // Gerar alertas e recomendações
    const alerts = generateAlerts(totalCost, finalPrice, profitMargin, competitorComparison);
    const priceRecommendation = generateRecommendation(profitMargin, competitorComparison);
    
    setResults({
      totalCost: totalCost,
      suggestedPrice: suggestedPrice,
      finalPrice: finalPrice,
      profitMargin: profitMargin,
      profitValue: profitValue,
      competitorComparison: competitorComparison,
      priceRecommendation: priceRecommendation,
      alerts: alerts
    });
  };

  const generateAlerts = (totalCost, finalPrice, profitMargin, competitorComparison) => {
    const alerts = [];
    
    if (profitMargin < 20) {
      alerts.push({
        type: 'error',
        message: 'Margem de lucro muito baixa (menos de 20%)',
        suggestion: 'Considere aumentar o preço ou reduzir custos'
      });
    } else if (profitMargin > 60) {
      alerts.push({
        type: 'warning',
        message: 'Margem de lucro muito alta (mais de 60%)',
        suggestion: 'Preço pode estar muito alto para o mercado escolar'
      });
    }
    
    if (Math.abs(competitorComparison) > 20) {
      alerts.push({
        type: 'warning',
        message: `Preço ${competitorComparison > 0 ? 'muito acima' : 'muito abaixo'} da concorrência`,
        suggestion: competitorComparison > 0 ? 
          'Verifique se o valor agregado justifica o preço' : 
          'Aproveite para destacar o preço competitivo'
      });
    }
    
    if (totalCost === 0) {
      alerts.push({
        type: 'info',
        message: 'Informe os custos para obter um cálculo preciso',
        suggestion: 'Preencha pelo menos o custo do produto'
      });
    }
    
    return alerts;
  };

  const generateRecommendation = (profitMargin, competitorComparison) => {
    if (profitMargin >= 25 && profitMargin <= 45 && Math.abs(competitorComparison) <= 15) {
      return 'Preço ideal! Boa margem e competitivo no mercado.';
    } else if (profitMargin < 20) {
      return 'Considere aumentar o preço para melhorar a margem de lucro.';
    } else if (competitorComparison > 20) {
      return 'Preço alto comparado à concorrência. Avalie reduzir ou destacar diferenciais.';
    } else if (competitorComparison < -20) {
      return 'Preço muito baixo. Você pode aumentar sem perder competitividade.';
    } else {
      return 'Preço adequado para o mercado escolar.';
    }
  };

  const handleInputChange = (field, value) => {
    setCalculation(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCategoryChange = (categoryKey) => {
    const category = categories[categoryKey];
    if (category) {
      setCalculation(prev => ({
        ...prev,
        category: categoryKey,
        desiredMargin: category.margin,
        operationalCosts: category.operationalCosts
      }));
    }
  };

  const saveCalculation = () => {
    if (!calculation.productName) {
      alert('Digite o nome do produto para salvar');
      return;
    }
    
    const savedCalc = {
      id: Date.now(),
      date: new Date().toLocaleDateString('pt-BR'),
      productName: calculation.productName,
      category: calculation.category,
      finalPrice: results.finalPrice,
      profitMargin: results.profitMargin,
      calculation: { ...calculation },
      results: { ...results }
    };
    
    setSavedCalculations(prev => [savedCalc, ...prev.slice(0, 4)]); // Manter apenas 5 últimos
    alert('Cálculo salvo com sucesso!');
  };

  const loadCalculation = (savedCalc) => {
    setCalculation(savedCalc.calculation);
  };

  const resetCalculation = () => {
    setCalculation({
      costPrice: '',
      packaging: '',
      transportation: '',
      storage: '',
      operationalCosts: 15,
      taxes: 8,
      marketingCosts: 5,
      desiredMargin: 30,
      productName: '',
      category: '',
      unitType: 'unidade',
      competitorPrice: '',
      marketPosition: 'medio'
    });
  };

  const handleRegisterProduct = () => {
    if (!calculation.productName) {
      alert('Digite o nome do produto antes de cadastrar');
      return;
    }
    
    const productData = {
      name: calculation.productName,
      category: calculation.category || 'outros',
      unit: calculation.unitType,
      cost_price: parseFloat(calculation.costPrice) || 0,
      sale_price: results.finalPrice,
      profit_margin: results.profitMargin,
      calculation_data: {
        costPrice: calculation.costPrice,
        packaging: calculation.packaging,
        transportation: calculation.transportation,
        storage: calculation.storage,
        operationalCosts: calculation.operationalCosts,
        taxes: calculation.taxes,
        marketingCosts: calculation.marketingCosts,
        desiredMargin: calculation.desiredMargin,
        competitorPrice: calculation.competitorPrice,
        marketPosition: calculation.marketPosition
      }
    };
    
    navigate('/dashboard/products/new', { 
      state: { 
        calculatorData: productData 
      } 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value.toFixed(1)}%`;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              <Calculator className="inline-block w-8 h-8 mr-3" />
              Calculadora de Preços
            </h1>
            <p className="text-blue-100">
              Precifique seus produtos de forma inteligente e competitiva
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{formatCurrency(results.finalPrice)}</div>
            <div className="text-blue-200">Preço sugerido</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário de entrada */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações do produto */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Package className="w-5 h-5 mr-2 text-blue-600" />
              Informações do Produto
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Produto
                </label>
                <input
                  type="text"
                  value={calculation.productName}
                  onChange={(e) => handleInputChange('productName', e.target.value)}
                  placeholder="Ex: Coxinha de frango"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria
                </label>
                <select
                  value={calculation.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma categoria</option>
                  {Object.entries(categories).map(([key, cat]) => (
                    <option key={key} value={key}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Custos diretos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Custos Diretos
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custo do Produto *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={calculation.costPrice}
                    onChange={(e) => handleInputChange('costPrice', e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embalagem
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={calculation.packaging}
                    onChange={(e) => handleInputChange('packaging', e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transporte
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={calculation.transportation}
                    onChange={(e) => handleInputChange('transportation', e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Armazenamento
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={calculation.storage}
                    onChange={(e) => handleInputChange('storage', e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Custos indiretos e margem */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center">
                <Percent className="w-5 h-5 mr-2 text-purple-600" />
                Custos Indiretos e Margem
              </h2>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {showAdvanced ? 'Simplificar' : 'Avançado'}
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Margem de Lucro Desejada
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    value={calculation.desiredMargin}
                    onChange={(e) => handleInputChange('desiredMargin', e.target.value)}
                    className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="absolute right-3 top-2 text-gray-500">%</span>
                </div>
              </div>
              
              {showAdvanced && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custos Operacionais
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        value={calculation.operationalCosts}
                        onChange={(e) => handleInputChange('operationalCosts', e.target.value)}
                        className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Impostos e Taxas
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        value={calculation.taxes}
                        onChange={(e) => handleInputChange('taxes', e.target.value)}
                        className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marketing e Promoção
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        value={calculation.marketingCosts}
                        onChange={(e) => handleInputChange('marketingCosts', e.target.value)}
                        className="w-full pr-8 pl-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Análise de mercado */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-orange-600" />
              Análise de Mercado
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preço da Concorrência
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-gray-500">R$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={calculation.competitorPrice}
                    onChange={(e) => handleInputChange('competitorPrice', e.target.value)}
                    placeholder="0,00"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Posicionamento de Mercado
                </label>
                <select
                  value={calculation.marketPosition}
                  onChange={(e) => handleInputChange('marketPosition', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {Object.entries(marketPositions).map(([key, position]) => (
                    <option key={key} value={key}>{position.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={saveCalculation}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4 mr-2" />
              Salvar Cálculo
            </button>
            
            <button
              onClick={resetCalculation}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Limpar
            </button>
          </div>
        </div>

        {/* Resultados */}
        <div className="space-y-6">
          {/* Resultado principal */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-green-600" />
              Resultado
            </h2>
            
            <div className="space-y-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">
                  {formatCurrency(results.finalPrice)}
                </div>
                <div className="text-sm text-green-700">Preço sugerido</div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-semibold text-blue-600">
                    {formatPercent(results.profitMargin)}
                  </div>
                  <div className="text-xs text-blue-700">Margem real</div>
                </div>
                
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-semibold text-purple-600">
                    {formatCurrency(results.profitValue)}
                  </div>
                  <div className="text-xs text-purple-700">Lucro por unidade</div>
                </div>
              </div>
              
              {calculation.competitorPrice && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">vs Concorrência:</span>
                    <span className={`font-semibold ${
                      results.competitorComparison > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {results.competitorComparison > 0 ? '+' : ''}{formatPercent(results.competitorComparison)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Breakdown de custos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-3">Breakdown de Custos</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Custo Total:</span>
                <span className="font-medium">{formatCurrency(results.totalCost)}</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Margem aplicada:</span>
                <span className="font-medium">{calculation.desiredMargin}%</span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Ajuste de mercado:</span>
                <span className="font-medium">
                  {marketPositions[calculation.marketPosition].name}
                </span>
              </div>
              
              <hr className="my-2" />
              
              <div className="flex justify-between text-lg">
                <span className="font-semibold">Preço Final:</span>
                <span className="font-bold text-green-600">
                  {formatCurrency(results.finalPrice)}
                </span>
              </div>
            </div>
          </div>

          {/* Alertas e recomendações */}
          {(results.alerts.length > 0 || results.priceRecommendation) && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <Lightbulb className="w-5 h-5 mr-2 text-yellow-600" />
                Recomendações
              </h3>
              
              {results.priceRecommendation && (
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start">
                    <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">{results.priceRecommendation}</p>
                  </div>
                </div>
              )}
              
              {results.alerts.map((alert, index) => (
                <div key={index} className={`mb-3 p-3 rounded-lg ${
                  alert.type === 'error' ? 'bg-red-50' : 
                  alert.type === 'warning' ? 'bg-yellow-50' : 'bg-blue-50'
                }`}>
                  <div className="flex items-start">
                    {alert.type === 'error' ? (
                      <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : alert.type === 'warning' ? (
                      <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`text-sm font-medium ${
                        alert.type === 'error' ? 'text-red-800' : 
                        alert.type === 'warning' ? 'text-yellow-800' : 'text-blue-800'
                      }`}>
                        {alert.message}
                      </p>
                      <p className={`text-xs mt-1 ${
                        alert.type === 'error' ? 'text-red-600' : 
                        alert.type === 'warning' ? 'text-yellow-600' : 'text-blue-600'
                      }`}>
                        {alert.suggestion}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cálculos salvos */}
          {savedCalculations.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-3 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-gray-600" />
                Cálculos Recentes
              </h3>
              
              <div className="space-y-2">
                {savedCalculations.slice(0, 3).map((calc) => (
                  <div key={calc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{calc.productName}</p>
                      <p className="text-xs text-gray-500">
                        {calc.date} • {formatCurrency(calc.finalPrice)}
                      </p>
                    </div>
                    <button
                      onClick={() => loadCalculation(calc)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Carregar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dicas */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl p-6 text-white">
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2" />
              Dicas para Precificação
            </h3>
            
            <div className="space-y-2 text-sm">
              <p>• <strong>Margem ideal:</strong> Entre 25% e 45% para cantinas escolares</p>
              <p>• <strong>Pesquise concorrentes:</strong> Mantenha-se competitivo no mercado</p>
              <p>• <strong>Considere o público:</strong> Preços acessíveis para estudantes</p>
              <p>• <strong>Qualidade:</strong> Justifique preços maiores com melhor qualidade</p>
              <p>• <strong>Promoções:</strong> Use combos para aumentar ticket médio</p>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de análise comparativa */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
          Análise Comparativa de Preços
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Preço baixo */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-center mb-3">
              <h3 className="font-semibold text-gray-900">Preço Baixo</h3>
              <p className="text-sm text-gray-600">Estratégia competitiva</p>
            </div>
            
            <div className="text-center p-3 bg-red-50 rounded-lg mb-3">
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(results.suggestedPrice * 0.9)}
              </div>
              <div className="text-sm text-red-700">-10% do preço base</div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Margem:</span>
                <span className="font-medium">{formatPercent((results.profitMargin * 0.9) - 10)}</span>
              </div>
              <div className="flex justify-between">
                <span>Lucro:</span>
                <span className="font-medium">{formatCurrency(results.profitValue * 0.8)}</span>
              </div>
            </div>
            
            <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
              <strong>Quando usar:</strong> Alta concorrência, penetração de mercado
            </div>
          </div>

          {/* Preço equilibrado */}
          <div className="border-2 border-blue-500 rounded-lg p-4 relative">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
              <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                Recomendado
              </span>
            </div>
            
            <div className="text-center mb-3 mt-2">
              <h3 className="font-semibold text-gray-900">Preço Equilibrado</h3>
              <p className="text-sm text-gray-600">Melhor custo-benefício</p>
            </div>
            
            <div className="text-center p-3 bg-blue-50 rounded-lg mb-3">
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(results.finalPrice)}
              </div>
              <div className="text-sm text-blue-700">Preço calculado</div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Margem:</span>
                <span className="font-medium">{formatPercent(results.profitMargin)}</span>
              </div>
              <div className="flex justify-between">
                <span>Lucro:</span>
                <span className="font-medium">{formatCurrency(results.profitValue)}</span>
              </div>
            </div>
            
            <div className="mt-3 p-2 bg-green-50 rounded text-xs text-green-800">
              <strong>Quando usar:</strong> Posicionamento balanceado, público geral
            </div>
          </div>

          {/* Preço premium */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-center mb-3">
              <h3 className="font-semibold text-gray-900">Preço Premium</h3>
              <p className="text-sm text-gray-600">Diferenciação por qualidade</p>
            </div>
            
            <div className="text-center p-3 bg-purple-50 rounded-lg mb-3">
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(results.suggestedPrice * 1.15)}
              </div>
              <div className="text-sm text-purple-700">+15% do preço base</div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Margem:</span>
                <span className="font-medium">{formatPercent((results.profitMargin * 1.15) + 5)}</span>
              </div>
              <div className="flex justify-between">
                <span>Lucro:</span>
                <span className="font-medium">{formatCurrency(results.profitValue * 1.3)}</span>
              </div>
            </div>
            
            <div className="mt-3 p-2 bg-purple-50 rounded text-xs text-purple-800">
              <strong>Quando usar:</strong> Produtos diferenciados, alta qualidade
            </div>
          </div>
        </div>
      </div>

      {/* Simulador de vendas */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
          Simulador de Vendas
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { qty: 50, period: 'dia', color: 'blue' },
            { qty: 300, period: 'semana', color: 'green' },
            { qty: 1200, period: 'mês', color: 'purple' }
          ].map((scenario, index) => (
            <div key={index} className={`border border-${scenario.color}-200 rounded-lg p-4`}>
              <div className="text-center mb-3">
                <h3 className="font-semibold text-gray-900">{scenario.qty} vendas/{scenario.period}</h3>
              </div>
              
              <div className={`text-center p-3 bg-${scenario.color}-50 rounded-lg mb-3`}>
                <div className={`text-xl font-bold text-${scenario.color}-600`}>
                  {formatCurrency(results.finalPrice * scenario.qty)}
                </div>
                <div className={`text-sm text-${scenario.color}-700`}>Faturamento</div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Lucro total:</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(results.profitValue * scenario.qty)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Custo total:</span>
                  <span className="font-medium text-red-600">
                    {formatCurrency(results.totalCost * scenario.qty)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600 text-center">
            <strong>Dica:</strong> Use essas projeções para definir metas de vendas e acompanhar o desempenho do produto no mercado.
          </p>
        </div>
      </div>

      {/* Exportar e integrar */}
      <div className="bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Pronto para vender?</h3>
            <p className="text-indigo-100">
              Cadastre este produto no sistema com o preço calculado automaticamente.
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
            <button 
              onClick={handleRegisterProduct}
              className="flex items-center px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Produto
            </button>
            <button className="flex items-center px-4 py-2 bg-indigo-400 text-white rounded-lg hover:bg-indigo-300 transition-colors font-medium">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Adicionar ao PDV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceCalculator;